import { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { CalendarPlus, Calendar as CalendarIcon, Download } from "lucide-react";
import { CALENDAR_SPONSOR } from "../../lib/calendarSponsor";
import { api } from "../../lib/api";

const trackClick = (release, target) => {
  if (localStorage.getItem("rp_token")) return;
  api.post("/track/calendar-click", { release_id: `${release.type}:${release.tmdb_id}`, target }).catch(() => {});
};

/**
 * "Add to calendar" widget for an upcoming release.
 *
 * Props:
 *   release  — release doc (must have tmdb_id, title, release_date, type).
 *   variant  — "icon" (32×32 overlay) | "button" (pill with label).
 *   sponsor  — optional { name, logo? }. Defaults to CALENDAR_SPONSOR
 *              (currently null — no partnership).
 *
 * Popover order adapts to the visitor's platform:
 *   - Apple ecosystem (iOS, iPadOS, macOS) → `.ics` first (native Add
 *     to Calendar prompt in Calendar.app / iCloud sync).
 *   - Everyone else → Google Agenda first (works out of the box on
 *     Android, most Windows / Chromebook browsers).
 */

const isAppleUA = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  // iPad on iPadOS 13+ reports Mac in userAgent → also check touch.
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const iPadOS =
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  const macOS = /Mac/i.test(platform) && !iOS;
  return iOS || iPadOS || macOS;
};

// Only native Safari on iOS/macOS hooks the .ics mime-type and shows
// the "Add this event" native prompt. Chrome iOS (`CriOS`), Firefox
// iOS (`FxiOS`), Edge iOS (`EdgiOS`) do NOT — the click is a dead
// end. For those, Google Agenda is the reliable path, so we surface
// it first.
const isAppleNativeSafari = () => {
  if (!isAppleUA()) return false;
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isNonSafari = /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|GSA/i.test(ua);
  return !isNonSafari;
};

const pad = (n) => String(n).padStart(2, "0");

const toCompactDate = (iso) => {
  // iso = "YYYY-MM-DD" → "YYYYMMDD"
  if (!iso || iso.length < 10) return null;
  return `${iso.slice(0, 4)}${iso.slice(5, 7)}${iso.slice(8, 10)}`;
};

const buildGoogleCalendarUrl = (release) => {
  const start = toCompactDate(release.release_date);
  if (!start) return null;
  // All-day event: DTEND is exclusive day-after.
  const y = Number(release.release_date.slice(0, 4));
  const m = Number(release.release_date.slice(5, 7));
  const d = Number(release.release_date.slice(8, 10));
  const endDate = new Date(Date.UTC(y, m - 1, d + 1));
  const end = `${endDate.getUTCFullYear()}${pad(endDate.getUTCMonth() + 1)}${pad(endDate.getUTCDate())}`;
  const platforms = release.platforms || [];
  const platform = platforms[0];
  const kind = release.type || "Film";
  const canal =
    kind === "Film" && (!platform || platform === "Cinéma" || platform === "À venir")
      ? "Sortie cinéma en France"
      : platform
      ? `Sortie sur ${platform}`
      : "Sortie";
  const siteOrigin =
    typeof window !== "undefined" ? window.location.origin : "https://rougepremiere.com";
  const slug = `${kind.toLowerCase()}-${release.tmdb_id}`;
  const detailUrl = `${siteOrigin}/releases/${slug}`;
  const details = [`${release.title} — ${canal}`, "", `Voir la fiche : ${detailUrl}`].join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Sortie : ${release.title}`,
    dates: `${start}/${end}`,
    details,
    location: canal,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildIcsUrl = (release) => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend}/api/releases/${release.tmdb_id}/calendar.ics?type=${encodeURIComponent(
    release.type,
  )}`;
};

export const AddToCalendar = ({ release, variant = "icon", sponsor = CALENDAR_SPONSOR }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });
  const appleFirst = useMemo(() => isAppleNativeSafari(), []);

  // Close on click outside OR on Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Compute popover position from the trigger's viewport rect. Fixed
  // positioning + portal → the popover escapes any `overflow-hidden`
  // ancestor (poster card, grid row, etc.) and lands on top of
  // everything at z-index 60.
  const recompute = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const POPOVER_WIDTH = 256; // matches w-64
    const POPOVER_EST_HEIGHT = 200; // rough; popover is ~180-220px
    const margin = 8;
    // Prefer opening BELOW the trigger, aligned to its right edge.
    let top = rect.bottom + margin;
    let left = rect.right - POPOVER_WIDTH;
    // Flip above if it would clip the viewport bottom.
    if (top + POPOVER_EST_HEIGHT > window.innerHeight - 8) {
      top = Math.max(8, rect.top - margin - POPOVER_EST_HEIGHT);
    }
    // Nudge horizontally to stay inside the viewport.
    if (left < 8) left = 8;
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - 8 - POPOVER_WIDTH;
    }
    setPos({ top, left, ready: true });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    // Track scroll / resize so the popover follows the trigger.
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const googleHref = useMemo(() => buildGoogleCalendarUrl(release), [release]);
  const icsHref = useMemo(() => buildIcsUrl(release), [release]);

  // Trigger — same visual grammar as vote pills / share buttons.
  const trigger =
    variant === "button" ? (
      <div className="inline-flex flex-col gap-1" ref={triggerRef}>
        <button
          type="button"
          data-testid={`add-to-calendar-btn-${release.tmdb_id}`}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-[var(--line-strong)] text-[11px] uppercase tracking-[0.15em] font-medium text-neutral-800 hover:text-white hover:bg-black hover:border-black transition-colors"
        >
          <CalendarPlus className="h-3.5 w-3.5" />
          Ajouter au calendrier
        </button>
        {sponsor && (
          <span
            data-testid={`add-to-calendar-sponsor-${release.tmdb_id}`}
            className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 pl-3"
          >
            avec <span className="text-neutral-700 font-semibold">{sponsor.name}</span>
          </span>
        )}
      </div>
    ) : (
      <button
        type="button"
        ref={triggerRef}
        data-testid={`add-to-calendar-icon-${release.tmdb_id}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Ajouter au calendrier"
        title="Ajouter au calendrier"
        className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[var(--line-strong)] bg-white/95 backdrop-blur text-neutral-700 hover:text-white hover:bg-black hover:border-black transition-colors shadow-[0_2px_8px_-4px_rgba(0,0,0,0.25)]"
      >
        <CalendarPlus className="h-4 w-4" />
      </button>
    );

  const ctaApple = (
    <a
      key="ics"
      href={icsHref}
      download
      onClick={() => trackClick(release, "ics")}
      data-testid={`add-to-calendar-ics-${release.tmdb_id}`}
      className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-800 hover:bg-neutral-50 hover:text-[var(--red)] border-b border-[var(--line)] last:border-0"
    >
      <Download className="h-4 w-4 text-neutral-500" />
      <span className="flex-1">Apple · Outlook (.ics)</span>
      <span className="text-[10px] text-neutral-400 uppercase tracking-wider">Télécharger</span>
    </a>
  );

  const ctaGoogle = googleHref && (
    <a
      key="google"
      href={googleHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackClick(release, "google")}
      data-testid={`add-to-calendar-google-${release.tmdb_id}`}
      className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-800 hover:bg-neutral-50 hover:text-[var(--red)] border-b border-[var(--line)] last:border-0"
    >
      <CalendarIcon className="h-4 w-4 text-neutral-500" />
      <span className="flex-1">Google Agenda</span>
      <span className="text-[10px] text-neutral-400 uppercase tracking-wider">Ouvrir</span>
    </a>
  );

  const ctas = appleFirst ? [ctaApple, ctaGoogle] : [ctaGoogle, ctaApple];

  const popover =
    open && pos.ready
      ? createPortal(
          <div
            ref={popoverRef}
            data-testid={`add-to-calendar-popover-${release.tmdb_id}`}
            style={{ position: "fixed", top: pos.top, left: pos.left, width: 256 }}
            className="z-[60] pointer-events-auto bg-white border border-[var(--line-strong)] rounded-md shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)] overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-[var(--line)]">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                Rappel
              </div>
              <div className="text-sm font-semibold text-neutral-900 mt-0.5 line-clamp-1">
                {release.title}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Sortie le {release.release_date}
              </div>
              {sponsor && (
                <div
                  data-testid={`add-to-calendar-popover-sponsor-${release.tmdb_id}`}
                  className="mt-2 pt-2 border-t border-dashed border-[var(--line)] flex items-center justify-between gap-3"
                >
                  <span className="text-[9px] uppercase tracking-[0.25em] text-neutral-400 shrink-0">
                    Proposé par
                  </span>
                  {sponsor.logo ? (
                    <div className="flex items-center gap-1.5">{sponsor.logo}</div>
                  ) : (
                    <span className="text-[11px] font-bold tracking-tight text-neutral-900">
                      {sponsor.name}
                    </span>
                  )}
                </div>
              )}
            </div>
            {ctas}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative inline-block">
      {trigger}
      {popover}
    </div>
  );
};

export default AddToCalendar;
